import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  Plus,
  Phone,
  Filter
} from 'lucide-react';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { useToast } from '@/components/common/Toast';
import CallViewHeader from './CallViewHeader';
import CallStatsCards from './CallStatsCards';

import { CALL_TYPE_CONFIG, STATUS_CONFIG } from '@/theme/corporateTheme';

// Calendar-specific type config with darker event colors
const CALENDAR_TYPE_CONFIG = {
  CALL1: { label: 'Discovery', color: 'bg-blue-700', lightColor: 'bg-blue-50', textColor: 'text-blue-800' },
  CALL2: { label: 'Demo', color: 'bg-indigo-700', lightColor: 'bg-indigo-50', textColor: 'text-indigo-800' },
  CALL3: { label: 'Follow-up', color: 'bg-emerald-700', lightColor: 'bg-emerald-50', textColor: 'text-emerald-800' },
};

interface CallEvent {
  id: string;
  contactId: string;
  callType: 'CALL1' | 'CALL2' | 'CALL3';
  dueDate: string;
  status: 'OPEN' | 'DONE' | 'SKIPPED';
  contact?: {
    firstName: string;
    lastName: string;
    account?: {
      name: string;
    };
  };
  callPackData?: {
    notes?: string;
    duration?: number;
  };
}

export default function CallCalendarView() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calls, setCalls] = useState<CallEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'month' | 'week' | 'day'>('month');
  const [filterStatus, setFilterStatus] = useState<'all' | 'OPEN' | 'DONE'>('all');

  useEffect(() => {
    loadCalls();
  }, []);

  const loadCalls = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/call-tasks`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setCalls(result.data || []);
      }
    } catch (error) {
      console.error('Error loading calls:', error);
      addToast({
        type: 'error',
        title: 'Error',
        description: 'Failed to load calls',
      });
    } finally {
      setLoading(false);
    }
  };

  // Calendar calculations
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);

  const previousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get calls for a specific day
  const getCallsForDay = (day: number) => {
    const targetDate = new Date(year, month, day);
    const targetDateStr = targetDate.toISOString().split('T')[0];
    
    return calls.filter(call => {
      if (filterStatus !== 'all' && call.status !== filterStatus) return false;
      const callDate = new Date(call.dueDate).toISOString().split('T')[0];
      return callDate === targetDateStr;
    });
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Render calendar days
  const renderCalendar = () => {
    const days = [];
    
    // Empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(
        <div key={`empty-${i}`} className="min-h-24 border border-gray-200 bg-gray-50"></div>
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayCalls = getCallsForDay(day);
      const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
      
      days.push(
        <div
          key={day}
          className={`min-h-24 border border-gray-200 p-2 hover:bg-gray-50 transition-colors ${
            isToday ? 'bg-blue-50 border-blue-300' : 'bg-white'
          }`}
        >
          <div className="flex justify-between items-start mb-1">
            <span className={`text-sm font-semibold ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
              {day}
            </span>
            {dayCalls.length > 0 && (
              <Badge variant="outline" className="text-xs px-1 py-0">
                {dayCalls.length}
              </Badge>
            )}
          </div>
          
          <div className="space-y-1">
            {dayCalls.slice(0, 3).map(call => (
              <button
                key={call.id}
                onClick={() => navigate(`/calls/${call.id}`)}
                className={`w-full text-left px-2 py-1 rounded text-xs ${
                  CALENDAR_TYPE_CONFIG[call.callType].lightColor
                } ${CALENDAR_TYPE_CONFIG[call.callType].textColor} hover:opacity-80 transition-opacity border ${
                  call.callType === 'CALL1' ? 'border-blue-200' : 
                  call.callType === 'CALL2' ? 'border-indigo-200' : 'border-emerald-200'
                }`}
              >
                <div className="font-medium truncate">
                  {call.contact?.firstName} {call.contact?.lastName}
                </div>
                <div className="text-xs opacity-75 truncate">
                  {CALENDAR_TYPE_CONFIG[call.callType].label}
                </div>
              </button>
            ))}
            {dayCalls.length > 3 && (
              <div className="text-xs text-gray-500 text-center">
                +{dayCalls.length - 3} more
              </div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  // Calculate stats
  const stats = {
    total: calls.length,
    scheduled: calls.filter(c => c.status === 'OPEN').length,
    completed: calls.filter(c => c.status === 'DONE').length,
    overdue: calls.filter(c => {
      if (c.status !== 'OPEN') return false;
      return new Date(c.dueDate) < new Date();
    }).length,
    dueToday: calls.filter(c => {
      const due = new Date(c.dueDate);
      const today = new Date();
      return due.toDateString() === today.toDateString();
    }).length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-400 animate-pulse" />
          <p className="text-gray-500">Loading calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Unified Header */}
      <CallViewHeader
        currentView="calendar"
        title="Call Management"
        subtitle="Schedule and manage your calls with calendar view"
        showCreateButton={false}
      />

      {/* Unified Stats */}
      <CallStatsCards stats={stats} variant="compact" />

      {/* Controls */}
      <Card className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* Navigation */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={previousMonth}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToToday}>
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={nextMonth}>
              <ChevronRight className="w-4 h-4" />
            </Button>
            <h2 className="text-xl font-bold ml-4">
              {monthNames[month]} {year}
            </h2>
          </div>

          {/* Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Calls</option>
              <option value="OPEN">Scheduled Only</option>
              <option value="DONE">Completed Only</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Legend */}
      <Card className="p-4">
        <div className="flex items-center gap-6">
          <span className="text-sm font-medium text-gray-700">Call Types:</span>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-blue-600 border border-blue-700"></div>
            <span className="text-sm text-gray-700">Discovery</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-indigo-600 border border-indigo-700"></div>
            <span className="text-sm text-gray-700">Demo</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-emerald-600 border border-emerald-700"></div>
            <span className="text-sm text-gray-700">Follow-up</span>
          </div>
        </div>
      </Card>

      {/* Calendar Grid */}
      <Card className="p-4">
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-0 mb-2">
          {dayNames.map(day => (
            <div key={day} className="text-center font-semibold text-sm text-gray-700 p-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7 gap-0 border-t border-gray-200">
          {renderCalendar()}
        </div>
      </Card>
    </div>
  );
}
